import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import {
  X, Search, Pencil, Eye, FileText, ChevronDown, ChevronUp,
  BookOpen, FlaskConical, Shield, GraduationCap, Globe,
  FolderKanban, Check, Loader2, ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const ICON_MAP = { BookOpen, FlaskConical, Shield, GraduationCap, Globe };

/* ─── Inline Template Preview/Editor ─── */
const TemplateViewEdit = ({ template, workflowId, onUpdated }) => {
  const [mode, setMode] = useState("preview");
  const [content, setContent] = useState(template.template || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/workflows/${workflowId}/templates`, {
        templates: [{ stage: template.stage, type: template.type, template: content }],
      }, { withCredentials: true });
      setSaved(true);
      onUpdated?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border/40 rounded-xl overflow-hidden bg-white" data-testid={`template-${template.stage}-${template.type}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50/80 border-b border-border/40">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-3.5 h-3.5 text-foreground/40 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground truncate">{template.type}</p>
            <p className="text-[10px] text-foreground/40 truncate">{template.brief}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="flex bg-white border border-border/60 rounded-lg p-0.5">
            <button onClick={() => setMode("preview")} className={`px-2.5 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 ${mode === "preview" ? "bg-gray-100 text-foreground" : "text-foreground/40"}`} data-testid="tmpl-preview-tab">
              <Eye className="w-3 h-3" />Preview
            </button>
            <button onClick={() => setMode("edit")} className={`px-2.5 py-1 rounded-md text-[10px] font-medium flex items-center gap-1 ${mode === "edit" ? "bg-gray-100 text-foreground" : "text-foreground/40"}`} data-testid="tmpl-edit-tab">
              <Pencil className="w-3 h-3" />Edit
            </button>
          </div>
          {mode === "edit" && (
            <button onClick={handleSave} disabled={saving} className="px-2.5 py-1 rounded-md text-[10px] font-semibold bg-[#014d7d] text-white hover:bg-[#013d66] flex items-center gap-1 disabled:opacity-50" data-testid="tmpl-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : null}
              {saving ? "Saving..." : saved ? "Saved!" : "Save"}
            </button>
          )}
        </div>
      </div>
      <div className="p-4 max-h-[400px] overflow-y-auto">
        {mode === "preview" ? (
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              components={{
                h2: ({ children }) => <h2 className="text-base font-heading font-bold text-foreground mt-0 mb-2 pb-1.5 border-b border-border/40">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-heading font-semibold text-foreground mt-3 mb-1.5">{children}</h3>,
                h4: ({ children }) => <h4 className="text-xs font-heading font-semibold text-foreground/80 mt-2 mb-1">{children}</h4>,
                p: ({ children }) => <p className="text-sm text-foreground/70 mb-2 leading-relaxed">{children}</p>,
                table: ({ children }) => <div className="overflow-x-auto mb-3"><table className="w-full text-xs border-collapse">{children}</table></div>,
                thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                th: ({ children }) => <th className="px-3 py-1.5 text-left font-semibold text-foreground/70 border border-border/40">{children}</th>,
                td: ({ children }) => <td className="px-3 py-1.5 text-foreground/60 border border-border/40">{children}</td>,
                ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5 text-sm text-foreground/70">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5 text-sm text-foreground/70">{children}</ol>,
                strong: ({ children }) => <strong className="font-semibold text-foreground/80">{children}</strong>,
                code: ({ children }) => <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono text-[#014d7d]">{children}</code>,
                pre: ({ children }) => <pre className="bg-gray-50 rounded-lg p-3 mb-3 overflow-x-auto text-xs">{children}</pre>,
              }}
            >{content}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={content} onChange={(e) => setContent(e.target.value)}
            className="w-full min-h-[250px] p-3 font-mono text-xs text-foreground border border-border/60 rounded-xl resize-y outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/30 bg-gray-50/50 leading-relaxed"
            data-testid="tmpl-edit-area"
          />
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════ */
export const TemplateManager = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedStages, setExpandedStages] = useState({});

  const fetchWorkflows = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workflows`, { withCredentials: true });
      const arr = Array.isArray(data) ? data : [];
      setWorkflows(arr);
      if (arr.length > 0 && !selectedWorkflow) setSelectedWorkflow(arr[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedWorkflow]);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const toggleStage = (idx) => setExpandedStages(prev => ({ ...prev, [idx]: !prev[idx] }));

  const stages = selectedWorkflow?.stages || [];
  const allTemplates = stages.flatMap((s, si) => (s.artifacts || [])?.map((a, ai) => ({ ...a, stage: s.name, stageIdx: si, artIdx: ai })));
  const filteredTemplates = search
    ? allTemplates?.filter(t => t.type.toLowerCase().includes(search.toLowerCase()) || t.stage.toLowerCase().includes(search.toLowerCase()) || t.brief?.toLowerCase().includes(search.toLowerCase()))
    : null;

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-foreground/40" /></div>;
  }

  return (
    <div className="h-full flex flex-col bg-[#fafaf8]" data-testid="template-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-white">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 text-foreground/50" />
          </button>
          <div>
            <h1 className="font-heading font-bold text-lg text-foreground">Template Manager</h1>
            <p className="text-xs text-foreground/50">Browse, preview, and edit artifact templates across all workflows</p>
          </div>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="h-9 pl-8 pr-4 text-xs border border-border/60 rounded-lg outline-none focus:ring-2 focus:ring-[#014d7d]/10 w-60"
            data-testid="template-search"
          />
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Sidebar — Workflow List */}
        <div className="w-72 border-r border-border/40 bg-white overflow-y-auto shrink-0" data-testid="workflow-sidebar">
          <div className="p-3 border-b border-border/40">
            <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-wider">Workflows ({workflows.length})</p>
          </div>
          {workflows?.map((wf) => {
            const Icon = ICON_MAP[wf.icon] || FolderKanban;
            const isSelected = selectedWorkflow?.id === wf.id;
            const templateCount = (wf.stages || []).reduce((sum, s) => sum + (s.artifacts?.length || 0), 0);
            return (
              <button
                key={wf.id}
                onClick={() => { setSelectedWorkflow(wf); setExpandedStages({}); setSearch(""); }}
                className={`w-full text-left px-4 py-3.5 border-b border-border/20 transition-all ${
                  isSelected ? "bg-[#014d7d]/[0.04] border-l-2 border-l-[#014d7d]" : "hover:bg-gray-50 border-l-2 border-l-transparent"
                }`}
                data-testid={`wf-sidebar-${wf.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${wf.color || "#014d7d"}14` }}>
                    <Icon className="w-4 h-4" style={{ color: wf.color || "#014d7d" }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold truncate ${isSelected ? "text-[#014d7d]" : "text-foreground"}`}>{wf.name}</p>
                    <p className="text-[10px] text-foreground/40">{(wf.stages || []).length} stages · {templateCount} templates</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="template-content">
          {selectedWorkflow && !search && (
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                {(() => { const Icon = ICON_MAP[selectedWorkflow.icon] || FolderKanban; return <Icon className="w-5 h-5" style={{ color: selectedWorkflow.color }} />; })()}
                <h2 className="font-heading font-bold text-base text-foreground">{selectedWorkflow.name}</h2>
              </div>
              <p className="text-xs text-foreground/50 ml-8">{selectedWorkflow.description}</p>
            </div>
          )}

          {/* Search results */}
          {search && filteredTemplates && (
            <div className="space-y-4">
              <p className="text-xs text-foreground/50 mb-2">{filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} matching "{search}"</p>
              {filteredTemplates?.map((t, i) => (
                <div key={i}>
                  <p className="text-[10px] font-bold text-foreground/40 uppercase tracking-wider mb-1.5">{t.stage} Stage</p>
                  <TemplateViewEdit template={t} workflowId={selectedWorkflow.id} onUpdated={fetchWorkflows} />
                </div>
              ))}
              {filteredTemplates.length === 0 && <p className="text-xs text-foreground/40 text-center py-8">No templates match your search.</p>}
            </div>
          )}

          {/* Stage-by-stage view */}
          {!search && stages?.map((stage, si) => {
            const isExpanded = expandedStages[si] !== false; // default expanded
            const arts = stage.artifacts || [];
            return (
              <div key={si} className="mb-6" data-testid={`stage-section-${si}`}>
                <button onClick={() => toggleStage(si)} className="flex items-center justify-between w-full py-2 group">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: selectedWorkflow.color || "#014d7d" }}>
                      {si + 1}
                    </div>
                    <h3 className="text-sm font-heading font-semibold text-foreground">{stage.name}</h3>
                    <span className="text-[10px] text-foreground/40">{arts.length} template{arts.length !== 1 ? "s" : ""}</span>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-foreground/30" /> : <ChevronDown className="w-4 h-4 text-foreground/30" />}
                </button>
                <p className="text-xs text-foreground/50 ml-8 mb-3">{stage.description}</p>
                {isExpanded && (
                  <div className="space-y-3 ml-8">
                    {arts?.map((art, ai) => (
                      <TemplateViewEdit
                        key={ai}
                        template={{ ...art, stage: stage.name }}
                        workflowId={selectedWorkflow.id}
                        onUpdated={fetchWorkflows}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
